import { Box, type ColumnsType, Flex, Table, Text } from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import type { SubProjectForKSModel } from '@/features/project/projects/_models/sub-project';
import { useId } from '@/hooks';
import { TABLE_BUTTON_WIDTH } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ExecutionProgressSummaryTableActions } from './ExecutionProgressSummaryTableActions';

type SubProjectForKs = z.infer<typeof SubProjectForKSModel>;
type SubProjectForKsColumns = ColumnsType<SubProjectForKs>;

type Props = {
  fundBusinessForLocalGovernmentSummaries: SubProjectForKs[] | undefined;
  columns: ColumnsType<SubProjectForKs>;
  editable: boolean;
};

const PINPOINT_SHEET_TYPE = 'KS_LOCAL_GOVERNMENT';

export const IncomeAndExpenditureTable: FC<Props> = ({
  fundBusinessForLocalGovernmentSummaries,
  columns,
  editable,
}) => {
  const { t } = useTranslation();
  const { id: projectId } = useId();

  return (
    <Flex vertical gap={8}>
      <Text text={t('income-and-expenditure-title')} />
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: PINPOINT_SHEET_TYPE,
          ksLocalGovernment: {
            path: { project: projectId },
            field: 'ks.local-government.income-and-expenditure-table',
          },
        })}
      >
        <Flex vertical>
          <Table
            enableSticky={false}
            dataSource={fundBusinessForLocalGovernmentSummaries}
            rowKey="index"
            bordered
            pagination={false}
            columns={[
              ...columns,
              ...(editable
                ? ([
                    {
                      title: '',
                      key: 'action',
                      align: 'center',
                      width: TABLE_BUTTON_WIDTH,
                      fixed: 'right',
                      render: () => (
                        <PublishedProjectMutableGuard>
                          <ExecutionProgressSummaryTableActions />
                        </PublishedProjectMutableGuard>
                      ),
                    },
                  ] satisfies SubProjectForKsColumns)
                : []),
            ]}
          />
          <Box alignSelf="end" paddingTop={4}>
            <Text
              color="secondary"
              text={t(
                'supplementary-for-local-government-income-and-expenditure',
              )}
            />
          </Box>
        </Flex>
      </PinpointMarker>
    </Flex>
  );
};
